import type { SubscriptionTier, PremiumFeature, User } from '@sharecost/shared';
import { FREE_GROUP_EXPENSE_LIMIT, GROUP_PROPAGATING_FEATURES } from '@sharecost/shared';
import { FREE_FOR_ALL_ENABLED } from '@/config/free-mode';

export function isPremiumTier(tier: SubscriptionTier | undefined): boolean {
  // Free-for-all campaign: every gate that funnels through this predicate
  // (usePremium, hasFeatureInGroup, checkGroupExpenseLimit) opens up.
  if (FREE_FOR_ALL_ENABLED) return true;

  return !!tier && tier !== 'FREE';
}

/**
 * Group creation is always allowed — monetization is via expense count limits.
 */
export function canCreateGroup(): boolean {
  return true;
}

export function isGroupPremium(
  members: { user?: Pick<User, 'subscription_tier' | 'is_virtual'> | null }[],
): boolean {
  return members.some(
    m => m.user && !m.user.is_virtual && isPremiumTier(m.user.subscription_tier),
  );
}

export function hasFeatureInGroup(
  userTier: SubscriptionTier | undefined,
  feature: PremiumFeature,
  members: { user?: Pick<User, 'subscription_tier' | 'is_virtual'> | null }[],
): boolean {
  if (isPremiumTier(userTier)) return true;
  if (GROUP_PROPAGATING_FEATURES.includes(feature)) {
    return isGroupPremium(members);
  }
  return false;
}

/**
 * Check if a group has reached the free expense limit.
 * Returns { allowed, currentCount, limit } for UI display.
 */
export function checkGroupExpenseLimit(
  userTier: SubscriptionTier | undefined,
  expenseCount: number,
  members: { user?: Pick<User, 'subscription_tier' | 'is_virtual'> | null }[],
): { allowed: boolean; currentCount: number; limit: number } {
  const limit = FREE_GROUP_EXPENSE_LIMIT;

  // Premium user or group with premium member → unlimited
  if (isPremiumTier(userTier) || isGroupPremium(members)) {
    return { allowed: true, currentCount: expenseCount, limit };
  }

  return { allowed: expenseCount < limit, currentCount: expenseCount, limit };
}
